/**
 * ConnectionStatusBadge — Shared status badge + statusToDot helper
 * Used across: MCP Servers, API Connections, Health Monitor, Detail
 */

import { Wifi, WifiOff, AlertTriangle, Zap } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ConnectionStatus } from '@/lib/mock-connections-data';

export function statusToDot(status: ConnectionStatus) {
  const map: Record<ConnectionStatus, 'healthy' | 'degraded' | 'down' | 'pending'> = {
    online: 'healthy',
    degraded: 'degraded',
    offline: 'down',
    testing: 'pending',
  };
  return map[status];
}

export const STATUS_BADGE_STYLES: Record<ConnectionStatus, string> = {
  online: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  degraded: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  offline: 'bg-red-500/10 text-red-600 dark:text-red-400',
  testing: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
};

const STATUS_ICONS: Record<ConnectionStatus, typeof Wifi> = {
  online: Wifi,
  degraded: AlertTriangle,
  offline: WifiOff,
  testing: Zap,
};

interface ConnectionStatusBadgeProps {
  status: ConnectionStatus;
  className?: string;
}

export function ConnectionStatusBadge({ status, className }: ConnectionStatusBadgeProps) {
  const Icon = STATUS_ICONS[status];
  return (
    <Badge variant="outline" className={cn('text-[9px]', STATUS_BADGE_STYLES[status], className)}>
      <Icon className="h-2.5 w-2.5 mr-0.5" />
      {status}
    </Badge>
  );
}
